import { useState, useEffect } from 'react';
import { Drawer, Table, Button, Typography, message } from 'antd';
import { laborFeeCostBearers, laborFeePaymentMethods, CurrencyList } from '@utils/constants';
import { formatCurrency } from '@utils/utils';
import { fetchPayBatchDetails } from '@services/Finance/FinanceLaborFee.service.js';
import config from '@/config';
import './PaymentApplicationDetailDrawer.less';

const { Text } = Typography;

const PaymentApplicationDetailDrawer = props => {
    const { open, onClose, batchNo, projectId } = props;

    // 弹窗内部状态
    const [loading, setLoading] = useState(false);
    const [batchDetails, setBatchDetails] = useState(null); // 批次详情数据
    const [approvalProcessList, setApprovalProcessList] = useState([]); // 审批流程列表（从 batchDetails 中获取）
    const [selectedCostBearerIndex, setSelectedCostBearerIndex] = useState(0); // 使用索引而非值来选择支付主体
    const defaultCurrencyText = CurrencyList.find(c => c.currency === 'CNY')?.name || '人民币';

    // 获取批次详情数据
    const getBatchDetails = async () => {
        if (!batchNo) return;

        try {
            const res = await fetchPayBatchDetails({ batchNo });
            if (res && res.status_code === 200 && res.data) {
                setBatchDetails(res.data);

                // 设置审批流程数据（接口已返回）
                if (res.data.approvalProcessList && res.data.approvalProcessList.length > 0) {
                    // 按顺序展示审批流程（不倒序）
                    setApprovalProcessList(res.data.approvalProcessList);
                }

                // 默认选中第一个支付主体（索引为0）
                if (res.data.projectLaborBatchPayment && res.data.projectLaborBatchPayment.length > 0) {
                    setSelectedCostBearerIndex(0);
                }
            }
        } catch (error) {
            console.error('获取批次详情失败:', error);
        }
    };

    // 弹窗打开时加载数据
    useEffect(() => {
        if (open && batchNo) {
            setLoading(true);
            getBatchDetails().finally(() => {
                setLoading(false);
            });
        }
    }, [open, batchNo]);

    // 根据币种代码获取货币单位信息
    const getCurrencyInfo = currencyCode => {
        const currency = CurrencyList.find(c => c.currency === currencyCode);
        return currency
            ? {
                  majorUnit: currency.majorUnit,
                  minorUnits: currency.minorUnits,
              }
            : {
                  majorUnit: '元',
                  minorUnits: ['角', '分'],
              };
    };

    // 数字金额转大写（带货币单位）
    const convertNumberToChineseCurrency = (num, unit = '元', minorUnits = ['角', '分']) => {
        const chnNumChar = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];

        const numStr = (parseFloat(num) || 0).toFixed(2);
        const [intPart, decimalPart] = numStr.split('.');
        const intNum = parseInt(intPart, 10);

        // 处理四位以内的数字转换
        const convertSectionToWords = num => {
            if (num === 0) return '';

            let result = '';
            const qian = Math.floor(num / 1000);
            const bai = Math.floor((num % 1000) / 100);
            const shi = Math.floor((num % 100) / 10);
            const ge = num % 10;

            if (qian > 0) {
                result += chnNumChar[qian] + '仟';
            }
            if (bai > 0) {
                result += chnNumChar[bai] + '佰';
            } else if (qian > 0 && (shi > 0 || ge > 0)) {
                result += '零';
            }
            if (shi > 0) {
                result += chnNumChar[shi] + '拾';
            } else if ((qian > 0 || bai > 0) && ge > 0) {
                if (shi === 0 && bai === 0) {
                    result += '零';
                }
            }
            if (ge > 0) {
                result += chnNumChar[ge];
            }

            return result;
        };

        // 处理万位以上的数字
        if (intNum === 0) {
            return `零${unit}整`;
        }

        let result = '';

        // 处理万位及以上
        if (intNum >= 10000) {
            const wan = Math.floor(intNum / 10000);
            const remainder = intNum % 10000;

            result += convertSectionToWords(wan) + '万';

            if (remainder > 0) {
                if (remainder < 1000) {
                    result += '零';
                }
                result += convertSectionToWords(remainder);
            }
        } else {
            result = convertSectionToWords(intNum);
        }

        // 清理多余的零
        result = result.replace(/零+/g, '零').replace(/零$/g, '').replace(/^零/, '');

        result += unit;

        // 处理小数部分
        if (decimalPart) {
            const minorUnit1 = minorUnits[0] || '角'; // 默认为角
            const minorUnit2 = minorUnits[1] || '分'; // 默认为分

            const digit1 = parseInt(decimalPart[0], 10);
            const digit2 = parseInt(decimalPart[1], 10);

            if (digit1 === 0 && digit2 === 0) {
                result += '整';
            } else {
                if (digit1 > 0) {
                    result += chnNumChar[digit1] + minorUnit1;
                } else {
                    if (digit2 > 0) {
                        result += '零';
                    }
                }
                if (digit2 > 0) {
                    result += chnNumChar[digit2] + minorUnit2;
                }
            }
        } else {
            result += '整';
        }

        return result;
    };

    // 根据币种代码转换数字金额为大写
    const convertNumberToChineseCurrencyByCurrency = (num, currencyCode) => {
        const { majorUnit, minorUnits } = getCurrencyInfo(currencyCode);
        return convertNumberToChineseCurrency(num, majorUnit, minorUnits);
    };

    // 获取支付主体名称
    const getCostBearerName = (value, currencyType = 0) => {
        // 如果是字符串类型，直接返回
        let name;
        if (typeof value === 'string') {
            name = value;
        } else {
            // 如果是数字类型，从常量中查找
            name = laborFeeCostBearers.find(b => b.value === value)?.label || value || '—';
        }

        // 防止重复添加"（外币）"标识
        const foreignCurrencyIndicator = '（外币）';
        const cleanName = name.endsWith(foreignCurrencyIndicator) ? name.slice(0, -4) : name;

        // 根据currencyType判断是否为外币支付
        if (currencyType === 1) {
            return `${cleanName}${foreignCurrencyIndicator}`;
        }

        return cleanName;
    };

    // 获取当前选中的支付主体数据
    const getCurrentCostBearerData = () => {
        if (!batchDetails || !batchDetails.projectLaborBatchPayment || selectedCostBearerIndex < 0) return null;
        return batchDetails.projectLaborBatchPayment[selectedCostBearerIndex];
    };

    const currentCostBearerData = getCurrentCostBearerData();

    // 获取外币实发额字段和单位
    const getForeignCurrencyInfo = paymentData => {
        // 从CurrencyList中获取所有可能的货币类型
        const currencyKeys = CurrencyList.map(currency => currency.currency);

        // 查找paymentData中存在的货币字段
        for (const currencyKey of currencyKeys) {
            // 检查是否存在该货币字段（例如 USD）
            if (paymentData[currencyKey] !== undefined && paymentData[currencyKey] !== null) {
                // 查找对应的文本字段 (例如 USD 对应 USDText)
                const textKey = `${currencyKey}Text`;
                const currencyText = paymentData[textKey] || paymentData.currencyText || currencyKey;

                return {
                    amount: paymentData[currencyKey],
                    unit: currencyText,
                    field: currencyKey,
                };
            }
        }

        // 默认返回
        return {
            amount: paymentData.laborFeeAmount,
            unit: paymentData.currencyText || 'CNY',
            field: 'laborFeeAmount',
        };
    };

    // 下载当前明细打印
    const handleDownloadDetail = () => {
        if (!batchNo || selectedCostBearerIndex < 0) {
            message.warning('请选择支付主体');
            return;
        }

        // 获取当前支付主体的币种类型
        const currentData = getCurrentCostBearerData();
        const currencyType = currentData?.currencyType || 0; // 1-外币 0-非外币

        // 获取支付主体的value值（从 laborFeeCostBearers 常量中查找）
        let costBearerValue = currentData?.costBearer;
        // 如果 costBearer 是字符串，需要查找对应的 value
        if (typeof currentData?.costBearer === 'string') {
            const costBearerItem = laborFeeCostBearers.find(b => b.label === currentData?.costBearer);
            costBearerValue = costBearerItem ? costBearerItem.value : currentData?.costBearer;
        }

        // 构建下载链接
        const params = new URLSearchParams({
            batchNo: batchNo,
            costBearerInt: costBearerValue,
            currencyType: currencyType,
        });

        const downloadUrl = `${config.API_PREFIX}/user/laborAudit/pay-batch-details-pdf?${params.toString()}`;
        // console.log(downloadUrl)
        // return
        window.open(downloadUrl, '_blank');
    };

    // 付款申请明细表格列定义（根据是否外币动态调整）
    const getDetailColumns = () => {
        const isForeignCurrency = currentCostBearerData?.currencyType === 1;

        const baseColumns = [
            {
                title: '姓名',
                dataIndex: 'memUsername',
                width: 100,
                fixed: 'left',
            },
            {
                title: '支付主体',
                dataIndex: 'costBearer',
                width: 120,
                render: value => getCostBearerName(value, currentCostBearerData?.currencyType) || <Text type="secondary">—</Text>, // 使用当前支付主体的currencyType
            },
            {
                title: '付款方式',
                dataIndex: 'paymentMethod',
                width: 120,
                render: value => {
                    if (typeof value === 'string') return value;
                    const method = laborFeePaymentMethods.find(m => m.value === value);
                    return method ? method.label : <Text type="secondary">—</Text>;
                },
            },
            {
                title: '支付币种',
                dataIndex: 'currencyText',
                width: 100,
                render: value => value || <Text type="secondary">—</Text>,
            },
        ];

        // 外币付款显示外币相关列
        if (isForeignCurrency) {
            baseColumns.push(
                {
                    title: '外币实发额',
                    dataIndex: 'foreignExchangeAmount',
                    width: 120,
                    render: value => (value !== null && value !== undefined ? formatCurrency(parseFloat(value)) : <Text type="secondary">—</Text>),
                },
                {
                    title: '折合人民币实发金额',
                    dataIndex: 'laborFeeAmount',
                    width: 150,
                    render: value => (value !== null && value !== undefined ? `¥${formatCurrency(parseFloat(value))}` : <Text type="secondary">—</Text>),
                },
                {
                    title: '折合人民币个人所得税',
                    dataIndex: 'individualTaxAmount',
                    width: 160,
                    render: value => (value !== null && value !== undefined ? `¥${formatCurrency(parseFloat(value))}` : <Text type="secondary">—</Text>),
                },
                {
                    title: '折合人民币实发劳务费',
                    dataIndex: 'hejiAmount',
                    width: 160,
                    render: value => (value !== null && value !== undefined ? `¥${formatCurrency(parseFloat(value))}` : <Text type="secondary">—</Text>),
                },
                {
                    title: '开户行',
                    dataIndex: 'bankName',
                    width: 180,
                    render: value => value || <Text type="secondary">—</Text>,
                },
                {
                    title: '银行卡号',
                    dataIndex: 'bankCardNo',
                    width: 180,
                    render: value => value || <Text type="secondary">—</Text>,
                },
            );
        } else {
            // 非外币付款显示人民币相关列
            baseColumns.push(
                {
                    title: '实发劳务费（元）',
                    dataIndex: 'laborFeeAmount',
                    width: 140,
                    render: value => (value !== null && value !== undefined ? `¥${formatCurrency(parseFloat(value))}` : <Text type="secondary">—</Text>),
                },
                {
                    title: '个人所得税（元）',
                    dataIndex: 'individualTaxAmount',
                    width: 140,
                    render: value => (value !== null && value !== undefined ? `¥${formatCurrency(parseFloat(value))}` : <Text type="secondary">—</Text>),
                },
                {
                    title: '含税劳务费（元）',
                    dataIndex: 'hejiAmount',
                    width: 140,
                    render: value => (value !== null && value !== undefined ? `¥${formatCurrency(parseFloat(value))}` : <Text type="secondary">—</Text>),
                },
                {
                    title: '开户行',
                    dataIndex: 'bankName',
                    width: 180,
                    render: value => value || <Text type="secondary">—</Text>,
                },
                {
                    title: '银行卡号',
                    dataIndex: 'bankCardNo',
                    width: 180,
                    render: value => value || <Text type="secondary">—</Text>,
                },
            );
        }

        return baseColumns;
    };

    // 流程日志表格列定义
    const getApprovalColumns = () => [
        {
            title: '流程节点',
            dataIndex: 'nodeName',
            width: 150,
            render: value => value || '—',
        },
        {
            title: '审批人',
            dataIndex: 'approvalByName',
            width: 120,
            render: value => value || '—',
        },
        {
            title: '状态',
            dataIndex: 'approvalStatusName',
            width: 100,
            render: value => value || '—',
        },
        {
            title: '审批意见',
            dataIndex: 'rejectedReason',
            width: 200,
            render: value => value || '—',
        },
        {
            title: '审批时间',
            dataIndex: 'approvalAt',
            width: 160,
            render: value => value || '—',
        },
    ];

    // 生成支付主体选择标签
    const renderCostBearerSelector = () => {
        if (!batchDetails || !batchDetails.projectLaborBatchPayment) return null;

        return (
            <div
                style={{
                    padding: '0 24px',
                    borderBottom: '1px solid #f0f0f0',
                    background: '#fff',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    height: 48,
                    lineHeight: '48px',
                }}
            >
                <span style={{ marginRight: 8, color: '#000', fontWeight: 600 }}>选择支付主体：</span>
                {batchDetails.projectLaborBatchPayment.map((cb, index) => {
                    // 先判断是否是外币支付，然后获取名称
                    const name = getCostBearerName(cb.costBearer, cb.currencyType);
                    const isSelected = index === selectedCostBearerIndex;

                    return (
                        <span key={`${cb.costBearer}-${index}`} style={{ marginRight: 16, display: 'inline-block', height: '100%' }}>
                            <span
                                style={{
                                    color: isSelected ? '#1890ff' : '#000',
                                    cursor: 'pointer',
                                    fontWeight: isSelected ? 600 : 400,
                                    borderBottom: isSelected ? '2px solid #1890ff' : 'none', // 只添加底部边框
                                    display: 'inline-block',
                                    height: '100%',
                                }}
                                onClick={() => setSelectedCostBearerIndex(index)}
                            >
                                {name}
                            </span>
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <Drawer
            closable={{ placement: 'end' }}
            title={<div style={{ textAlign: 'center', width: '100%' }}>付款申请明细</div>}
            placement="bottom"
            onClose={onClose}
            open={open}
            size="100vh"
            styles={{
                body: { padding: 0, overflow: 'hidden' },
                header: { borderBottom: '1px solid #f0f0f0' },
                wrapper: { height: '100vh' },
            }}
            className="payment-application-detail-drawer"
        >
            <div style={{ display: 'flex', height: '100%' }}>
                {/* 主要内容区域 */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                    {/* 选择支付主体 */}
                    {renderCostBearerSelector()}

                    {/* 内容区域 */}
                    <div style={{ padding: '24px', position: 'relative' }}>
                        {/* 下载按钮 */}
                        <div style={{ position: 'absolute', right: '30px', top: '20px' }}>
                            <Button onClick={handleDownloadDetail} type="link">
                                下载当前明细打印
                            </Button>
                        </div>
                        {/* 付款批次信息 */}
                        <div className="info-section">
                            <div className="section-title">付款申请批次信息</div>
                            <div className="section-content">
                                <div>
                                    <div>
                                        付款申请批次：<span>{batchDetails?.batchTimeStr || '—'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        经办人：<span>{batchDetails?.handlerName || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 项目信息 */}
                        <div className="info-section">
                            <div className="section-title">项目信息</div>
                            <div className="section-content">
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <div>
                                        项目名称：<span>{batchDetails?.conferenceName || '—'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        氚云编号：<span>{batchDetails?.h3yunNo || '—'}</span>
                                    </div>
                                </div>
                                <div>
                                    <div>
                                        项目编号：<span>{batchDetails?.projectCode || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 支付主体信息 */}
                        {currentCostBearerData && (
                            <div className="info-section">
                                <div className="section-title">支付主体：{getCostBearerName(currentCostBearerData.costBearer, currentCostBearerData.currencyType)}</div>
                                <div className="section-content">
                                    <div>
                                        <div>
                                            需要劳务费人数：<span>{currentCostBearerData.expertSize || 0}人</span>
                                        </div>
                                    </div>
                                    <div></div>
                                    {currentCostBearerData.currencyType === 1 ? (
                                        <>
                                            <div>
                                                <div>
                                                    外币实发额合计金额（{getForeignCurrencyInfo(currentCostBearerData).unit}）：<span>{formatCurrency(parseFloat(getForeignCurrencyInfo(currentCostBearerData).amount || 0))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {getForeignCurrencyInfo(currentCostBearerData).unit}
                                                        {convertNumberToChineseCurrencyByCurrency(parseFloat(getForeignCurrencyInfo(currentCostBearerData).amount || 0), currentCostBearerData.currencyText || 'CNY')}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    折合人民币实发劳务费合计金额（元）：<span>{formatCurrency(parseFloat(currentCostBearerData.laborFeeAmount || 0))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {defaultCurrencyText}
                                                        {convertNumberToChineseCurrency(parseFloat(currentCostBearerData.laborFeeAmount || 0))}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    折合人民币个人所得税合计金额（元）：<span>{formatCurrency(parseFloat(currentCostBearerData.individualTaxAmount || 0))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {defaultCurrencyText}
                                                        {convertNumberToChineseCurrency(parseFloat(currentCostBearerData.individualTaxAmount || 0))}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    折合人民币含税劳务费合计金额（元）：<span>{formatCurrency((parseFloat(currentCostBearerData.laborFeeAmount || 0) + parseFloat(currentCostBearerData.individualTaxAmount || 0)).toFixed(2))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {defaultCurrencyText}
                                                        {convertNumberToChineseCurrency(parseFloat((currentCostBearerData.laborFeeAmount || 0) + parseFloat(currentCostBearerData.individualTaxAmount || 0)).toFixed(2))}
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div>
                                                <div>
                                                    实发劳务费合计金额（元）：<span>{formatCurrency(parseFloat(currentCostBearerData.laborFeeAmount || 0))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {defaultCurrencyText}
                                                        {convertNumberToChineseCurrency(parseFloat(currentCostBearerData.laborFeeAmount || 0))}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    个人所得税合计金额（元）：<span>{formatCurrency(parseFloat(currentCostBearerData.individualTaxAmount || 0))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {defaultCurrencyText}
                                                        {convertNumberToChineseCurrency(parseFloat(currentCostBearerData.individualTaxAmount || 0))}
                                                    </span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    含税劳务费合计金额（元）：<span>{formatCurrency((parseFloat(currentCostBearerData.laborFeeAmount || 0) + parseFloat(currentCostBearerData.individualTaxAmount || 0)).toFixed(2))}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    金额（大写）：
                                                    <span>
                                                        {defaultCurrencyText}
                                                        {convertNumberToChineseCurrency((parseFloat(currentCostBearerData.laborFeeAmount || 0) + parseFloat(currentCostBearerData.individualTaxAmount || 0)).toFixed(2))}
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 付款申请明细 */}
                        {/* 注：当前接口暂未返回明细数据，等待后端接口完善后再展示 */}
                        <div className="info-section">
                            <div className="section-title">付款申请明细</div>
                            <Table
                                columns={getDetailColumns()}
                                dataSource={currentCostBearerData?.recordsList || []}
                                rowKey={(record, index) => `detail_${index}_${record.memUsername || record.expertName}`}
                                loading={loading}
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                                size="small"
                                locale={{
                                    emptyText: currentCostBearerData?.recordsList && currentCostBearerData?.recordsList.length > 0 ? '' : '暂无明细数据',
                                }}
                            />
                        </div>

                        {/* 流程日志 */}
                        <div className="info-section">
                            <div className="section-title">流程日志</div>
                            <Table
                                columns={getApprovalColumns()}
                                dataSource={approvalProcessList}
                                rowKey={record => `approval_${record.nodeId}`}
                                loading={loading}
                                pagination={false}
                                size="small"
                                locale={{
                                    emptyText: '暂无审批流程数据',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default PaymentApplicationDetailDrawer;
